import Microgame from '../engine/Microgame.js';

export default class FlipGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Retourne !`, en: `Flip!`, es: `¡Voltea!` },
            controls: { fr: `Glisse ton doigt de bas en haut (puis relâche)`, en: `Swipe up with your finger (then release)`, es: `Desliza hacia arriba (y suelta)` },
            objective: { fr: `Fais faire un tour complet à l'objet pour qu'il retombe droit`, en: `Make the object do a full flip and land upright`, es: `Haz que el objeto dé una vuelta completa y caiga derecho` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" stroke-width="2"><path d="m3 16 4 4 4-4"/><path d="M7 20V4"/><path d="m21 8-4-4-4 4"/><path d="M17 4v16"/></svg>`,
            actions: [
                { type: 'wait', duration: 200 },
                { type: 'hold', x: 22, y: 74, duration: 120, holdDuration: 920 },
                { type: 'wait', duration: 320 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.state = 'playing'; // playing, landed
        
        // Difficulty scaling
        this.speedMult = engine.speedMultiplier || 1.0;
        
        // Map x4 difficulty to the old x3.5 to soften the peak as requested
        const effectiveMult = 1 + (this.speedMult - 1) * (2.5 / 3);
        const adjustedSpeed = 1 + (effectiveMult - 1) * 0.25; // Smoother curve
        
        // Apply physics constraints proportionally to the attenuated speed
        this.airTime = 2000 / adjustedSpeed;
        
        // Rule applied: To make GameEngine's timer act as `timeLimit`, we simply multiply by speedMult since GameEngine will divide by it.
        // We already scaled `airTime`, so we don't divide by `adjustedSpeed` again.
        const timeLimit = this.airTime + 800; // Gives them enough time to react / see the result
        
        this.duration = timeLimit * this.speedMult;
        
        this.elapsedTime = 0;
        this.x = 10;
        this.groundLevel = 75;
        this.y = this.groundLevel;
        this.rotation = 0;
        
        this.isHolding = false;
        
        // Required rotational speeds for backflips
        // Passive guarantees landing upside down (crash) if no input is made
        this.passiveSpeed = 180 / this.airTime; // degrees per ms
        // Active guarantees up to 720 extra degrees (much more controllable than 1080)
        this.activeSpeed = 720 / this.airTime; // degrees per ms
    }

    setup() {
        this.engine.ui.instruction.textContent = "HOLD TO FLIP!";
        
        this.container.style.overflow = 'hidden';

        // Planet / Moon
        const planet = document.createElement('div');
        planet.style.position = 'absolute';
        planet.style.width = '150px';
        planet.style.height = '150px';
        planet.style.background = '#ffffff';
        planet.style.opacity = '0.08';
        planet.style.borderRadius = '50%';
        planet.style.right = '10%';
        planet.style.top = '10%';
        planet.style.boxShadow = `0 0 60px ${this.secondaryColor}`;
        this.container.appendChild(planet);
        
        // Environment details - subtle stars
        for (let i = 0; i < 15; i++) {
            const star = document.createElement('div');
            star.style.position = 'absolute';
            star.style.left = `${Math.random() * 100}%`;
            star.style.top = `${Math.random() * 60}%`;
            star.style.width = '2px';
            star.style.height = '2px';
            star.style.background = '#fff';
            star.style.opacity = Math.random() * 0.6 + 0.1;
            this.container.appendChild(star);
        }

        // Ramp
        const ramp = document.createElement('div');
        ramp.style.position = 'absolute';
        ramp.style.left = '-10%';
        ramp.style.top = `${this.groundLevel + 2}%`;
        ramp.style.width = '25%'; 
        ramp.style.height = '50%';
        ramp.style.background = `rgba(0,0,0,0.2)`;
        ramp.style.borderTop = `4px solid ${this.primaryColor}`;
        ramp.style.transform = 'skewY(-15deg)';
        ramp.style.zIndex = '5';
        this.container.appendChild(ramp);

        // Landing Pad Base
        const pad = document.createElement('div');
        pad.style.position = 'absolute';
        pad.style.left = '80%';
        pad.style.top = `${this.groundLevel + 2}%`;
        pad.style.width = '25%'; 
        pad.style.height = '50%';
        pad.style.background = `rgba(0,0,0,0.2)`;
        pad.style.borderTop = `4px solid ${this.primaryColor}`;
        this.container.appendChild(pad);

        // Target Indicator on pad for visual precision
        const target = document.createElement('div');
        target.style.position = 'absolute';
        target.style.left = '84%';
        target.style.top = `${this.groundLevel + 3}%`; 
        target.style.width = '12%'; 
        target.style.height = '4px';
        target.style.background = '#fff';
        target.style.boxShadow = `0 0 10px #fff`;
        target.style.borderRadius = '2px';
        this.container.appendChild(target);

        // Player Vehicle / Stunt Car
        this.player = document.createElement('div');
        this.player.style.position = 'absolute';
        this.player.style.width = '50px';
        this.player.style.height = '25px';
        this.player.style.background = this.secondaryColor;
        this.player.style.borderRadius = '5px 15px 5px 5px';
        this.player.style.boxShadow = `0 4px 0 rgba(0,0,0,0.2)`;
        this.player.style.zIndex = '10';

        // Window / Cockpit
        const win = document.createElement('div');
        win.style.position = 'absolute';
        win.style.right = '5px';
        win.style.top = '5px';
        win.style.width = '15px';
        win.style.height = '10px';
        win.style.background = 'white';
        win.style.opacity = '0.8';
        win.style.borderRadius = '2px 8px 2px 2px';
        this.player.appendChild(win);

        // Wheels
        const w1 = document.createElement('div');
        w1.style.position = 'absolute';
        w1.style.left = '5px';
        w1.style.bottom = '-8px';
        w1.style.width = '14px';
        w1.style.height = '14px';
        w1.style.background = '#222';
        w1.style.border = `2px solid ${this.primaryColor}`;
        w1.style.borderRadius = '50%';
        w1.style.boxSizing = 'border-box';
        this.player.appendChild(w1);

        const w2 = document.createElement('div');
        w2.style.position = 'absolute';
        w2.style.right = '5px';
        w2.style.bottom = '-8px';
        w2.style.width = '14px';
        w2.style.height = '14px';
        w2.style.background = '#222';
        w2.style.border = `2px solid ${this.primaryColor}`;
        w2.style.borderRadius = '50%';
        w2.style.boxSizing = 'border-box';
        this.player.appendChild(w2);

        // Booster visually integrated
        this.exhaust = document.createElement('div');
        this.exhaust.style.position = 'absolute';
        this.exhaust.style.width = '8px';
        this.exhaust.style.height = '14px';
        this.exhaust.style.left = '5px';
        this.exhaust.style.bottom = '-14px';
        this.exhaust.style.background = '#ffeb3b';
        this.exhaust.style.borderRadius = '5px 5px 10px 10px';
        this.exhaust.style.boxShadow = '0 0 10px #ff9800';
        this.exhaust.style.opacity = '0';
        this.exhaust.style.transition = 'opacity 0.1s';
        this.player.appendChild(this.exhaust);

        this.container.appendChild(this.player);

        // Initial Transform explicitly set
        this.player.style.left = `${this.x}%`;
        this.player.style.top = `${this.y}%`;
        this.player.style.transform = `translate(-50%, -50%) rotate(0deg)`;

        this.onDown = this.onDown.bind(this);
        this.onUp = this.onUp.bind(this);

        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
        this.engine.input.element.addEventListener('pointercancel', this.onUp);
    }

    onDown(e) {
        if (!this.active || this.state !== 'playing') return;
        this.isHolding = true;
        this.exhaust.style.opacity = '1';
        this.engine.ui.instruction.textContent = "FLIPPING!";
    }

    onUp(e) {
        this.isHolding = false;
        if (this.state === 'playing') {
            this.exhaust.style.opacity = '0';
            this.engine.ui.instruction.textContent = "HOLD TO FLIP!";
        }
    }

    createTrail() {
        const trail = document.createElement('div');
        trail.style.position = 'absolute';
        trail.style.left = `${this.x}%`;
        trail.style.top = `${this.y}%`;
        trail.style.width = '50px';
        trail.style.height = '25px';
        trail.style.background = this.primaryColor;
        trail.style.borderRadius = '5px 15px 5px 5px';
        trail.style.transform = `translate(-50%, -50%) rotate(${this.rotation}deg)`;
        trail.style.opacity = '0.4';
        trail.style.pointerEvents = 'none';
        trail.style.transition = 'all 0.3s ease-out';
        this.container.appendChild(trail);
        
        requestAnimationFrame(() => {
            trail.style.transform = `translate(-50%, -50%) rotate(${this.rotation - 15}deg) scale(0.6)`;
            trail.style.opacity = '0';
        });
        
        setTimeout(() => {
            if (trail.parentNode) trail.remove();
        }, 300);
    }

    createExplosion(color) {
        for (let i = 0; i < 8; i++) {
            const p = document.createElement('div');
            p.style.position = 'absolute';
            p.style.left = `${this.x}%`;
            p.style.top = `${this.y}%`;
            p.style.width = '12px';
            p.style.height = '12px';
            p.style.background = color;
            p.style.borderRadius = '50%';
            p.style.opacity = '1';
            
            const angle = Math.random() * Math.PI * 2;
            const dist = 30 + Math.random() * 50;
            const tx = Math.cos(angle) * dist;
            const ty = Math.sin(angle) * dist;
            
            p.style.transition = 'all 0.5s cubic-bezier(0.1, 0.8, 0.2, 1)';
            p.style.zIndex = '20';
            this.container.appendChild(p);
            
            requestAnimationFrame(() => {
                p.style.transform = `translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px)) scale(0)`;
                p.style.opacity = '0';
            });
            
            setTimeout(() => {
                if (p.parentNode) p.remove();
            }, 500);
        }
    }

    land() {
        this.state = 'landed';
        this.isHolding = false;
        this.exhaust.style.opacity = '0';
        
        // Freeze Game Engine Timer immediately to prevent TimeOuts after landing!
        this.engine.timer = Infinity;
        
        let finalRot = ((this.rotation % 360) + 360) % 360;
        
        // Ensure outcome element exists inside container to bypass GameEngine UI override
        const outcomeText = document.createElement('div');
        outcomeText.style.position = 'absolute';
        outcomeText.style.top = '30%';
        outcomeText.style.left = '50%';
        outcomeText.style.transform = 'translate(-50%, -50%)';
        outcomeText.style.fontSize = '2.5rem';
        outcomeText.style.fontWeight = 'bold';
        outcomeText.style.textAlign = 'center';
        outcomeText.style.zIndex = '100';
        outcomeText.style.textShadow = '0 0 20px #000';
        this.container.appendChild(outcomeText);

        // finalRot 0 is flat. 
        // Backward (nose up) is 360 -> 330 (which is -30). 
        // Forward (nose down) is 0 -> 10.
        if (finalRot >= 330 || finalRot <= 10) {
            this.player.style.transform = `translate(-50%, -50%) rotate(0deg) scale(1.1, 0.9)`;
            this.player.style.boxShadow = `0 0 30px ${this.primaryColor}`;
            outcomeText.textContent = "PERFECT LANDING!";
            outcomeText.style.color = this.primaryColor;
            this.createExplosion(this.primaryColor);
            
            setTimeout(() => this.win(), 600);
        } else {
            this.player.style.transform = `translate(-50%, -50%) rotate(${this.rotation}deg) scale(1.2, 0.4)`;
            this.player.style.background = '#e74c3c';
            outcomeText.textContent = "CRASHED!";
            outcomeText.style.color = '#e74c3c';
            this.createExplosion('#e74c3c');
            
            setTimeout(() => this.lose(), 600);
        }
    }

    update(dt) {
        if (!this.active) return;
        
        if (this.state === 'playing') {
            this.elapsedTime += dt;
            let progress = this.elapsedTime / this.airTime;
            if (progress > 1) progress = 1;
            
            // X Parabola (10% to 90%)
            this.x = 10 + progress * 80;
            
            // Y Parabola (apex at 20%)
            // groundLevel = 75, peak = 20, difference = 55 -> 4 * 55 = 220
            this.y = 220 * Math.pow(progress - 0.5, 2) + 20;

            let currentSpeed = this.passiveSpeed;
            if (this.isHolding) {
                currentSpeed += this.activeSpeed;
                
                this.trailTimer = (this.trailTimer || 0) + dt;
                if (this.trailTimer > 40) { 
                    this.createTrail();
                    this.trailTimer = 0;
                }
            }
            
            this.rotation += currentSpeed * dt;
            
            this.player.style.left = `${this.x}%`;
            this.player.style.top = `${this.y}%`;
            this.player.style.transform = `translate(-50%, -50%) rotate(${this.rotation}deg)`;
            
            if (progress >= 1) {
                this.land();
            }
        }
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
        this.engine.input.element.removeEventListener('pointercancel', this.onUp);
    }
}
